import { keyBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useGetUserDetailsQuery,
  UserDetails,
  useUpdateUserUIDetailsMutation,
} from '../services/projects/userDetailsApi';

import { AppState } from '../store/store';
import { navItems } from '../navItems';
import { useGetUserConfigsQuery } from '../services/projects/projectsApi';
import { useGetUserQuery } from '../services/portal/portalApi';
import { dynamicActivate, locales } from '../utils/i18n';

const browserLocale = navigator.language.split('-')[0];

const useUser = () => {
  const { user: authUser, exp } = useSelector((state: AppState) => state.auth);
  const [updateUserUIDetails] = useUpdateUserUIDetailsMutation();
  const isAuthenticated = !!authUser && exp > Date.now() / 1000;
  const [lang, setLang] = useState<string>();

  const {
    data: userSettings,
    isError: isErrorSettings,
    isLoading: isLoadingSettings,
  } = useGetUserQuery(authUser?.id, { skip: isAuthenticated && !authUser?.id });
  const {
    data: userConfig,
    isError,
    isLoading,
  } = useGetUserConfigsQuery(undefined, {
    skip: !isAuthenticated,
  });

  const { data: userDetails, isLoading: isLoadingDetails } = useGetUserDetailsQuery(undefined, {
    skip: !isAuthenticated,
  });

  const { ui_details: details } = userDetails ?? ({} as UserDetails);

  useEffect(() => {
    if (!isLoadingSettings && !isErrorSettings && userSettings?.locale) {
      const lang = locales[userSettings.locale] ? userSettings.locale : 'en';

      setLang(lang);
    } else if (isErrorSettings || (!isLoadingSettings && !userSettings?.locale)) {
      const lang = locales[browserLocale] ? browserLocale : 'en';
      console.log(`Using browser locale ${browserLocale} for translations`);
      setLang(lang);
    }
  }, [userSettings?.locale, isLoadingSettings, isErrorSettings]);

  useEffect(() => {
    if (lang) {
      dynamicActivate(lang);
    }
  }, [lang]);

  const nav = useMemo(() => {
    const navItemsByKey = keyBy(userConfig?.appConfig?.menu ?? [], 'navId');
    return navItems.filter((i) => {
      return navItemsByKey[i.id] || i.kind === 'divider';
    });
  }, [userConfig]);

  const canSendFeedback = useMemo(() => {
    return (
      isAuthenticated &&
      authUser?.email &&
      /^.+@(motrexllc\.com|stryten\.com)$/.test(authUser?.email ?? '')
    );
  }, [isAuthenticated, authUser?.email]);

  const isThirdParty = userConfig?.appConfig?.id === 'third-party';
  const saveUserUIDetails = useCallback(
    (newDetails: Partial<typeof details>) => {
      const tmp = { ...(details ?? {}), ...newDetails };
      updateUserUIDetails(tmp);
    },
    [details, updateUserUIDetails]
  );

  return {
    isAuthenticated,
    userConfig,
    userDetails,
    isLoading,
    isError,
    nav,
    auth: authUser,
    details,
    isLoadingDetails,
    isThirdParty,
    canSendFeedback,
    isLoadingSettings,
    saveUserUIDetails,
    settings: userSettings,
    isErrorSettings,
  };
};

export { useUser };
